import { CreateCityInput } from "@/types/gql/graphql";
import * as turf from "@turf/turf";

import { City, CityInput, Geometry, MapViewType } from "types";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { FieldErrors, FormProvider, useForm } from "react-hook-form";
import { isJson } from "helper/utils";
import { PaginatedCitiesProps, usePaginatedCitiesActions } from "../hooks/paginated-cities.hook";
import { useEffect, useMemo, useRef, useState } from "react";

import { AddCityActions } from "./FormActions";
import { CityOptions } from "./Options";
import { Coordinates } from "./Coordinates";
import { DataProvider } from "./DataProvider";
import { DecreeAnalysisConfig } from "./DecreeAnalysisConfig";
import { GeneralCityDetails } from "./GeneralDetails";
import { WazeConfig } from "./WazeConfig";
import { INITIAL_VIEW_STATE } from "constants/map.const";
import { OptionType } from "components/form-control/dropdown-autocomplete";
import { OsmSearchJsonV2 } from "services/common/osm.type";
import { WebMercatorViewport } from "@math.gl/web-mercator";
import WorldCities from "worldcities";
import { getAddress } from "@/services/web/mapbox.service";
import { getAllInfoByISO } from "iso-country-currency";
import { useCityQuery } from "services/web/map.queries";
import { useDebounce } from "hooks/lib-ui";

export interface AddCityModalProps extends PaginatedCitiesProps {
  onDismiss: React.Dispatch<React.SetStateAction<boolean>>;
  city?: City;
  open: boolean;
}

const defaultInputCityValue: CityInput = {
  name: "",
  zipCode: "",
  departement: "",
  state: "",
  country: "",
  countryCode: "",
  surfaceArea: "",
  dataProviderName: "",
  deviceIds: [],
  latitude: "",
  longitude: "",
  boundaries: "",
  currency: {
    code: "",
    symbol: "",
  },
  timezone: "",
  options: {
    live: false,
    offstreet: false,
    payment: false,
    curbZones: false,
    mapIntegration: false,
    mobileMap: false,
    decreeAnalysis: {
      enabled: false,
      emailFrom: "",
      emailTo: "",
    },
  },
};
const options = {
  shouldValidate: true,
};
interface TInputField<T> {
  name: T;
  value: string;
  options?:
    | Partial<{
        shouldValidate: boolean;
        shouldDirty: boolean;
        shouldTouch: boolean;
      }>
    | undefined;
}

type CityFormTab = "general" | "options" | "waze" | "decree" | "provider" | "perimetre";

const TAB_DEFINITIONS: { value: CityFormTab; label: string; hasError: (errors: FieldErrors<CityInput>) => boolean }[] =
  [
    {
      value: "general",
      label: "Général",
      hasError: (errors) =>
        [
          "name",
          "zipCode",
          "departement",
          "state",
          "country",
          "countryCode",
          "surfaceArea",
          "currency",
          "timezone",
        ].some((field) => field in errors),
    },
    { value: "options", label: "Options", hasError: () => false },
    { value: "waze", label: "Waze", hasError: (errors) => !!errors.options?.wazeFeedUrl },
    { value: "decree", label: "Arrêtés", hasError: (errors) => !!errors.options?.decreeAnalysis },
    {
      value: "provider",
      label: "Fournisseur de données",
      hasError: (errors) => "dataProviderName" in errors || "deviceIds" in errors,
    },
    {
      value: "perimetre",
      label: "Périmètre",
      hasError: (errors) => "longitude" in errors || "latitude" in errors || "boundaries" in errors,
    },
  ];

const MAP_REFERENCE_DIMENSIONS = { width: 549, height: 384 };

const fitViewStateToGeometry = (geometry: Geometry, width: number, height: number): MapViewType => {
  const [minLng, minLat, maxLng, maxLat] = turf.bbox({ type: "Feature", properties: {}, geometry });
  const { longitude, latitude, zoom } = new WebMercatorViewport({ ...INITIAL_VIEW_STATE, width, height }).fitBounds(
    [
      [minLng, minLat],
      [maxLng, maxLat],
    ],
    { padding: 2 },
  );
  return { ...INITIAL_VIEW_STATE, longitude, latitude, zoom, width, height };
};

export const AddCity = ({
  onDismiss,
  city,
  open,
  cityName: searchValue,
  setCurrentPage,
  currentPage,
}: AddCityModalProps) => {
  const [cityName, setCityName] = useState<string>("");
  const [viewState, setViewState] = useState<MapViewType>();
  const [enableProvider, setEnableProvider] = useState<boolean>(!!city?.details?.dataProvider?.name);
  const { createNewCity, updateCity } = usePaginatedCitiesActions({
    cityName: searchValue,
    setCurrentPage,
    currentPage,
  });
  const [selectedCity, setSelectedCity] = useState<OsmSearchJsonV2 | undefined>(undefined);
  const searchString = useDebounce(cityName, 500);
  const mapRef = useRef<HTMLDivElement | null>(null);
  const methods = useForm<CityInput>({ defaultValues: defaultInputCityValue, mode: "all" });
  const {
    setValue,
    getValues,
    clearErrors,
    formState: { errors, isSubmitting },
    handleSubmit,
  } = methods;
  const [activeTab, setActiveTab] = useState<CityFormTab>("general");
  const handleSearch = (value: string) => {
    setCityName(value);
    if (errors.name) {
      clearErrors("name");
    }
  };
  const { data, isLoading } = useCityQuery({
    name: searchString,
  });

  useEffect(() => {
    if (city) {
      setCityName(city.name);
      setValue("name", city.name, options);
      setValue("timezone", city.details?.timezone?.iana);
      let cityData: TInputField<keyof CityInput>[] = [
        { name: "boundaries", value: JSON.stringify(city.boundaries), options },
        { name: "latitude", value: city.position?.[1]?.toString() ?? "", options },
        { name: "longitude", value: city.position?.[0]?.toString() ?? "", options },
        { name: "zipCode", value: city.zipCode ?? "", options },
      ];

      if (city.details) {
        cityData = [
          ...cityData,
          { name: "surfaceArea", value: (city.details.surfaceArea ?? "").toString(), options },
          { name: "departement", value: city?.details?.departement ?? "", options },
          { name: "state", value: city?.details.state ?? "", options },
          { name: "country", value: city.details.country ?? "", options },
          { name: "countryCode", value: city.details?.countryCode ?? "", options },
        ];
      }

      cityData.forEach(({ name, value, options }) => setValue(name, value, options));

      setValue("currency.code", city?.details?.currency?.code ?? "", options);
      setValue("currency.symbol", city?.details?.currency?.symbol ?? "", options);
      setValue("options.offstreet", !!city?.details?.options?.offstreet, options);
      setValue("options.live", !!city?.details?.options?.live, options);
      setValue("options.payment", !!city?.details?.options?.payment, options);
      setValue("options.curbZones", !!city?.details?.options?.curbZones, options);
      setValue("options.mapIntegration", !!city?.details?.options?.mapIntegration, options);
      setValue("options.mobileMap", !!city?.details?.options?.mobileMap, options);
      setValue("options.decreeAnalysis.enabled", !!city?.details?.options?.decreeAnalysis?.enabled, options);
      setValue("options.decreeAnalysis.emailFrom", city?.details?.options?.decreeAnalysis?.emailFrom ?? "", options);
      setValue("options.decreeAnalysis.emailTo", city?.details?.options?.decreeAnalysis?.emailTo ?? "", options);
      setValue("options.wazeFeedUrl", city?.details?.options?.wazeFeedUrl ?? undefined, options);
      setValue("dataProviderName", city?.details?.dataProvider?.name ?? "", options);
      setValue(
        "deviceIds",
        city?.details?.dataProvider?.deviceIds?.map((device: string) => ({ device })) ?? [],
        options,
      );
    }
  }, [city, setValue]);

  useEffect(() => {
    setViewState(
      city?.boundaries
        ? fitViewStateToGeometry(city.boundaries, MAP_REFERENCE_DIMENSIONS.width, MAP_REFERENCE_DIMENSIONS.height)
        : { ...INITIAL_VIEW_STATE, ...MAP_REFERENCE_DIMENSIONS },
    );
  }, [city]);

  useEffect(() => {
    if (activeTab !== "perimetre") return;
    const rawBoundaries = getValues("boundaries");
    if (!isJson(rawBoundaries)) return;
    const width = mapRef.current?.clientWidth || MAP_REFERENCE_DIMENSIONS.width;
    const height = mapRef.current?.clientHeight || MAP_REFERENCE_DIMENSIONS.height;
    try {
      setViewState(fitViewStateToGeometry(JSON.parse(rawBoundaries) as Geometry, width, height));
    } catch {
      // The boundaries field is user-editable and may hold JSON that is not a valid geometry; skip reframing in that case.
    }
  }, [activeTab, getValues]);

  const handleSelectCity = async (value: string) => {
    if (data && data.length > 0) {
      const currentCityInfo = data[Number(value)];

      const { lat, lon } = currentCityInfo;
      const postcode = (
        await getAddress(
          {
            type: "Point",
            coordinates: [Number(lon), Number(lat)],
          },
          ["postcode"],
        )
      ).postcode;

      const nearestCity = WorldCities.getNearestCity(Number(lat), Number(lon));
      if (nearestCity) {
        setValue("timezone", nearestCity.timezone);
      }
      setCityName(currentCityInfo.name);
      const cityData: TInputField<keyof CityInput>[] = [
        { name: "departement", value: currentCityInfo.address.city ?? "", options },
        { name: "boundaries", value: JSON.stringify(currentCityInfo.geojson), options },
        { name: "latitude", value: currentCityInfo.lat, options },
        { name: "longitude", value: currentCityInfo.lon, options },
        { name: "state", value: currentCityInfo.address.state, options },
        { name: "country", value: currentCityInfo.address.country, options },
        { name: "countryCode", value: currentCityInfo.address.country_code, options },
        { name: "zipCode", value: postcode ?? "", options },
      ];
      cityData.forEach(({ name, value, options }) => setValue(name, value, options));

      setValue("currency.code", getAllInfoByISO(currentCityInfo.address.country_code).currency, options);
      setValue("currency.symbol", getAllInfoByISO(currentCityInfo.address.country_code).symbol, options);

      if (currentCityInfo.geojson.type === "Polygon") {
        const surface = turf.area(turf.polygon(currentCityInfo.geojson.coordinates));
        setValue("surfaceArea", surface.toString());
      }

      setSelectedCity(currentCityInfo);
    }
  };

  const result = useMemo(() => {
    const cities: Array<OptionType<string>> = [];
    if (data && data.length > 0) {
      data.forEach((item: OsmSearchJsonV2, index: number) => {
        cities.push({
          value: `${index}`,
          label: item.display_name,
        });
      });
    }
    return cities;
  }, [data]);

  const addCity = async (inputData: CityInput) => {
    const decreeAnalysisEnabled = !!inputData.options?.decreeAnalysis?.enabled;
    const trimmedOrUndefined = (value?: string) => (decreeAnalysisEnabled && value?.trim() ? value.trim() : undefined);
    const options = inputData.options
      ? {
          ...inputData.options,
          decreeAnalysis: {
            enabled: decreeAnalysisEnabled,
            emailFrom: trimmedOrUndefined(inputData.options.decreeAnalysis?.emailFrom),
            emailTo: trimmedOrUndefined(inputData.options.decreeAnalysis?.emailTo),
          },
        }
      : undefined;

    const timezone = inputData.timezone
      ? {
          iana: inputData.timezone,
        }
      : undefined;

    const details = {
      departement: inputData.departement,
      state: inputData.state,
      country: inputData.country,
      surfaceArea: +inputData.surfaceArea,
      osmId: +(selectedCity?.osm_id as number) || 0,
      countryCode: inputData.countryCode,
      options: options,
      currency: {
        code: inputData.currency.code,
        symbol: inputData.currency.symbol,
      },
      timezone: timezone,
    };

    const dataProvider = {
      name: inputData.dataProviderName,
      deviceIds: inputData.deviceIds.map((item: { device: string }) => item.device),
    };

    let newCity: CreateCityInput = {
      name: inputData.name,
      zipCode: inputData.zipCode,
      details,
      position: [+inputData.longitude, +inputData.latitude],
      boundaries: JSON.parse(inputData.boundaries) as Geometry,
    };

    if (enableProvider) {
      newCity = {
        ...newCity,
        details: {
          ...details,
          dataProvider,
        },
      };
    }

    if (city?.id) {
      const updatedCity = { ...newCity, id: city.id, image: city.image };
      await updateCity({ variables: { city: updatedCity } });
    } else {
      await createNewCity({ variables: { city: newCity } });
    }
    onDismiss(false);
  };

  const focusErroredTab = (formErrors: FieldErrors<CityInput>) => {
    const erroredTab = TAB_DEFINITIONS.find((tab) => tab.hasError(formErrors));
    if (erroredTab) {
      setActiveTab(erroredTab.value);
    }
  };

  return (
    <Dialog open={open}>
      <DialogContent className="sm:!w-2xl sm:!max-w-2xl flex h-[85vh] max-h-[48rem] flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>{city ? "Modifier une ville" : "Ajouter une ville"}</DialogTitle>
          <DialogDescription>Informations sur la ville</DialogDescription>
        </DialogHeader>
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(addCity, focusErroredTab)} className="flex min-h-0 flex-1 flex-col">
            <Tabs
              value={activeTab}
              onValueChange={(value) => setActiveTab(value as CityFormTab)}
              className="flex min-h-0 flex-1 flex-col"
            >
              <TabsList className="w-full shrink-0">
                {TAB_DEFINITIONS.map((tab) => {
                  const hasError = tab.hasError(errors);
                  return (
                    <TabsTrigger key={tab.value} value={tab.value} className={hasError ? "text-red-700" : ""}>
                      {tab.label}
                      {hasError ? <span className="text-red-700">&#9888;</span> : null}
                    </TabsTrigger>
                  );
                })}
              </TabsList>

              <div className="min-h-0 flex-1 overflow-y-auto">
                <TabsContent value="general">
                  <GeneralCityDetails
                    handleSearch={handleSearch}
                    handleSelectCity={handleSelectCity}
                    isLoading={isLoading}
                    result={result}
                    cityName={cityName}
                  />
                </TabsContent>

                <TabsContent value="options">
                  <CityOptions />
                </TabsContent>

                <TabsContent value="waze">
                  <WazeConfig />
                </TabsContent>

                <TabsContent value="decree">
                  <DecreeAnalysisConfig />
                </TabsContent>

                <TabsContent value="provider">
                  <DataProvider enableProvider={enableProvider} setEnableProvider={setEnableProvider} />
                </TabsContent>

                <TabsContent value="perimetre">
                  <Coordinates ref={mapRef} viewState={viewState} setViewState={setViewState} />
                </TabsContent>
              </div>
            </Tabs>

            <AddCityActions isSubmitting={isSubmitting} onDismiss={onDismiss} />
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
};
